import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Infinity, Users } from 'lucide-react';
import { scrollToSection } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

const EN_DEFAULTS = {
  hero_h1_main: 'Your wedding.',
  hero_h1_highlight: 'Beautifully invited.',
  hero_subtitle:
    'Stunning digital invitations your guests will actually love — with built-in RSVP tracking, guest management, and everything you need to keep your big day organised.',
  hero_cta_primary: 'Create Your Invite',
  hero_cta_secondary: 'See How It Works',
  hero_social_1: 'Loved by couples on their big day',
  hero_social_2: 'Unlimited guests included',
  hero_social_3: 'Day & evening guest support',
} as const;

type HeroStrings = Pick<
  HomepageStrings,
  | 'hero_h1_main'
  | 'hero_h1_highlight'
  | 'hero_subtitle'
  | 'hero_cta_primary'
  | 'hero_cta_secondary'
  | 'hero_social_1'
  | 'hero_social_2'
  | 'hero_social_3'
>;

export function HeroSection({ strings }: { strings?: HeroStrings }) {
  const s = strings ?? EN_DEFAULTS;

  return (
    <section id="hero" className="pt-32 pb-12 md:pt-40 md:pb-20 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl lg:text-5xl text-text-primary leading-[1.2] mb-6"
            style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, fontStyle: 'italic' }}
          >
            {s.hero_h1_main}{' '}
            <span className="text-primary">{s.hero_h1_highlight}</span>
          </h1>

          <p className="text-base md:text-xl text-text-secondary max-w-2xl mx-auto mb-10">
            {s.hero_subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/create" className="btn-hero btn-primary hover:shadow-lg">
              {s.hero_cta_primary}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="#how-it-works"
              onClick={(e) => scrollToSection(e, 'how-it-works')}
              className="btn-hero btn-secondary"
            >
              {s.hero_cta_secondary}
            </a>
          </div>
        </div>

        {/* Social Proof Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mt-14 text-text-secondary text-sm">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-primary" />
            <span>{s.hero_social_1}</span>
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-border" />
          <div className="flex items-center gap-2">
            <Infinity className="w-4 h-4 text-primary" />
            <span>{s.hero_social_2}</span>
          </div>
          <div className="hidden sm:block w-1 h-1 rounded-full bg-border" />
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            <span>{s.hero_social_3}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
